import { isPlatformServer } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  injectAsync,
  PLATFORM_ID,
  Signal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
  RouterState,
} from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { filter, map, of } from 'rxjs';

import { environment } from '../environments/environment';
import { AuthenticationService } from './auth/authentication.service';
import { LanguageService, TranslationKey } from './locales';
import { BACK_URL } from './routing';
import { CookieConsentService } from './shared/cookie-consent.service';
import { reportMissingIcons } from './shared/material-symbols-check';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.scss',
  imports: [
    RouterOutlet,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    TranslocoDirective,
    MatMenuModule,
    MatListModule,
    RouterLinkActive,
  ],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- kept by the v22 migration to preserve pre-v22 behavior
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { '[class.app-shell]': 'isShell' },
})
export class AppComponent {
  /**
   * True while rendering the app shell into index.html at build time (see app.config.server.ts).
   * The shell doesn't know the visitor's language, login state or page, so it hides everything that depends on them.
   */
  protected readonly isShell = isPlatformServer(inject(PLATFORM_ID));
  title = inject(Title);
  authService = inject(AuthenticationService);
  languageService = inject(LanguageService);
  cookieConsentService = inject(CookieConsentService);
  // loaded on demand to keep the dialog out of the initial bundle
  private matDialog = injectAsync(() => import('@angular/material/dialog').then((m) => m.MatDialog));
  private router = inject(Router);
  canGoBack = toSignal(
    // the shell looks like the home page, which has no back button
    this.isShell
      ? of(false)
      : this.router.events.pipe(
          filter((event) => event instanceof NavigationStart),
          map((event) => {
            const url = (event as NavigationStart).url;
            return url !== '/' && !url.startsWith('/?');
          }),
        ),
  );
  menuOpen = signal(false);

  constructor() {
    if (!environment.production) {
      afterNextRender(() => reportMissingIcons(document));
    }
    if (!this.isShell && this.cookieConsentService.isAnalyticsConfigured) {
      // set synchronously so the effect doesn't open a second dialog while the first one is loading
      let isDialogOpen = false;
      effect(() => {
        const preference = this.cookieConsentService.preference();
        const isBannerOpen = this.cookieConsentService.isBannerOpen();
        if (!isDialogOpen && (!preference || isBannerOpen)) {
          isDialogOpen = true;
          this.openCookieConsentDialog().then(
            (dialog) => dialog.afterClosed().subscribe(() => (isDialogOpen = false)),
            (error) => {
              isDialogOpen = false;
              console.error(error);
            },
          );
        }
      });
    }
  }

  private async openCookieConsentDialog() {
    const [matDialog, { CookieConsentDialogComponent }] = await Promise.all([
      this.matDialog(),
      import('./shared/cookie-consent-dialog/cookie-consent-dialog.component'),
    ]);
    return matDialog.open(CookieConsentDialogComponent, { disableClose: true, maxWidth: '480px' });
  }

  menuLinks = computed(() => {
    const routes = [
      { path: '', label: 'START_PAGE' as TranslationKey },
      { path: '/search', label: 'TO_TAKE_OVER' as TranslationKey },
      { path: '/user/advertisements', label: 'TO_LEAVE' as TranslationKey },
    ];
    if (this.authService.isAdmin()) {
      routes.push({ path: 'admin', label: 'ADMIN' as TranslationKey });
    }
    return routes;
  });
  footerSections: Signal<{ title?: string; links: { path?: string; externalLink?: string; label: string }[] }[]> =
    computed(() => {
      return [
        { title: 'PAGES' as TranslationKey, links: this.menuLinks() },
        {
          title: 'ABOUT' as TranslationKey,
          links: [
            { path: '/about', label: 'ABOUT_THIS_SITE' as TranslationKey },
            { externalLink: 'mailto:info@hallohoreca.be', label: 'CONTACT_US' as TranslationKey },
            { path: '/price-list', label: 'PRICING' as TranslationKey },
          ],
        },
        {
          links: [
            { path: '/privacy', label: 'COOKIES_AND_PRIVACY' as TranslationKey },
            { path: '/terms', label: 'GENERAL_CONDITIONS' as TranslationKey },
            { path: '/copyright', label: 'COPYRIGHT' as TranslationKey },
          ],
        },
      ];
    });
  socialLinks = [
    { icon: 'instagram', label: 'Instagram', url: 'https://www.instagram.com/hallo_horeca' },
    { icon: 'facebook', label: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61555853762503' },
  ];

  goBack() {
    // By default, go up one route segment. Can be changed by setting the 'backUrl' property inside the route data
    const route = this.findActivatedRoute(this.router.routerState);
    const backUrl: string = route.snapshot.data?.[BACK_URL] ?? '..';
    if (backUrl) {
      this.router.navigate([backUrl], { relativeTo: route });
    }
  }

  private findActivatedRoute(route: RouterState): ActivatedRoute {
    let child = route.root.firstChild!;
    while (child.firstChild) {
      child = child.firstChild;
    }
    return child;
  }

  toggleMenu() {
    this.menuOpen.update((open) => !open);
  }
}
